import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { ADMIN_COMPANY_PREFIX, ADMIN_REGION_PREFIX } from '@/utils/adminApi';
import {
  ensureAdminMiniProgramContext,
  getAdminMiniProgramContext,
} from '@/utils/adminMiniProgram';
import { getAdminRegionContext } from '@/utils/adminRegionContext';
import { clearAdminToken, getAdminToken } from '@/utils/auth';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  status?: number;
  result_body?: any;
  error_code?: number;
  error_msg?: string;
  trace_id?: string;
  span_id?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const errorMessage = errorInfo.error_msg;
          const errorCode = errorInfo.error_code;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      const token = getAdminToken();
      const headers = {
        ...(config.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
        headers.token = token;
      }

      const url = String(config.url || '');
      if (url.startsWith(ADMIN_COMPANY_PREFIX)) {
        const context =
          getAdminMiniProgramContext() ||
          (await ensureAdminMiniProgramContext());
        if (context?.id) {
          headers['X-Admin-Mini-Program-Id'] = context.id;
        }
        if (context?.companyId) {
          headers['X-Admin-Company-Id'] = context.companyId;
        }
      }
      if (url.startsWith(ADMIN_REGION_PREFIX)) {
        const regionContext = getAdminRegionContext();
        const miniProgramContext = getAdminMiniProgramContext();
        if (regionContext?.id) {
          headers['X-Admin-Region-Id'] = regionContext.id;
        }
        const miniProgramId =
          regionContext?.miniProgramId || miniProgramContext?.id;
        if (miniProgramId) {
          headers['X-Admin-Mini-Program-Id'] = miniProgramId;
        }
        const companyId =
          regionContext?.companyId || miniProgramContext?.companyId;
        if (companyId) {
          headers['X-Admin-Company-Id'] = companyId;
        }
      }

      return { ...config, headers };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const data = response?.data as ResponseStructure | undefined;

      if (
        data?.status === -1 &&
        (data.error_code === 1 || data.error_code === 2)
      ) {
        clearAdminToken();
      }
      return response;
    },
  ],
};
